import { useState, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CheckCircle, Eye, Phone, User, AlertCircle, Coins, Edit2, Trash2, Search, LogIn } from "lucide-react";
import { getAuthImgUrl, getStatusBadge, DocViewerDialog, TFTDialog, EditDriverDialog } from "./DriverDialogs";
import AdminResetPasswordButton from "./AdminResetPasswordButton";

function matchesSearch(driver, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  return [
    driver.name, driver.email, driver.phone, driver.cni_number, driver.license_number, driver.driver_status,
  ].some((v) => (v || "").toString().toLowerCase().includes(needle));
}

export default function DriverManagement({ pendingDrivers, allDrivers, onValidate, onDelete, actionLoading, onRefresh, onImpersonate }) {
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [tftDialog, setTftDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [search, setSearch] = useState("");

  const filteredPending = useMemo(() => pendingDrivers.filter((d) => matchesSearch(d, search)), [pendingDrivers, search]);
  const filteredAll = useMemo(() => allDrivers.filter((d) => matchesSearch(d, search)), [allDrivers, search]);

  const handleValidate = async (driverId, action) => {
    await onValidate(driverId, action, rejectReason);
    setSelectedDriver(null);
    setRejectReason("");
  };

  return (
    <div className="space-y-6">
      {/* Search bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-[#94A3B8] pointer-events-none" />
        <Input
          placeholder="Rechercher un chauffeur (nom, email, téléphone, CNI, permis)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9 border-[#CBD5E1]"
          data-testid="driver-search-input"
        />
        {search && (
          <p className="text-[10px] text-[#64748B] mt-1" data-testid="driver-search-summary">
            {filteredPending.length + filteredAll.length} chauffeur(s) correspondent
          </p>
        )}
      </div>

      {/* Pending Drivers */}
      <PendingDriversList
        drivers={filteredPending}
        onViewDocs={setSelectedDriver}
        onApprove={(id) => handleValidate(id, "approve")}
        actionLoading={actionLoading}
      />

      {/* All Drivers Table */}
      <DriversTable
        drivers={filteredAll}
        onViewDocs={setSelectedDriver}
        onEdit={setEditDialog}
        onTft={setTftDialog}
        onValidate={handleValidate}
        onDelete={onDelete}
        onImpersonate={onImpersonate}
        actionLoading={actionLoading}
      />

      {/* Dialogs */}
      <DocViewerDialog
        driver={selectedDriver}
        onClose={() => { setSelectedDriver(null); setRejectReason(""); }}
        onValidate={handleValidate}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        actionLoading={actionLoading}
        onRefresh={onRefresh}
      />
      <TFTDialog driver={tftDialog} onClose={() => setTftDialog(null)} onRefresh={onRefresh} />
      <EditDriverDialog driver={editDialog} onClose={() => setEditDialog(null)} onRefresh={onRefresh} />
    </div>
  );
}

function PendingDriversList({ drivers, onViewDocs, onApprove, actionLoading }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-[#0A2540] flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit' }}>
        <AlertCircle className="w-5 h-5 text-[#EAB308]" />
        En attente ({drivers.length})
      </h2>
      {drivers.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-8 text-center">
          <CheckCircle className="w-10 h-10 text-[#10B981] mx-auto mb-2" />
          <p className="text-sm text-[#64748B]">Aucun chauffeur en attente</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {drivers.map((driver) => {
            const badge = getStatusBadge(driver.driver_status);
            return (
              <div key={driver.id} className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid={`pending-driver-${driver.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <DriverAvatar driver={driver} badge={badge} />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onViewDocs(driver)} className="border-[#CBD5E1] text-[#0A2540]" data-testid={`view-driver-docs-${driver.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> Documents
                    </Button>
                    <Button size="sm" onClick={() => onApprove(driver.id)} disabled={actionLoading} className="bg-[#10B981] text-white hover:bg-[#059669]" data-testid={`approve-driver-${driver.id}`}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approuver
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DriversTable({ drivers, onViewDocs, onEdit, onTft, onValidate, onDelete, onImpersonate, actionLoading }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit' }}>Tous les chauffeurs ({drivers.length})</h2>
      <div className="bg-white rounded-xl border border-[#CBD5E1] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#CBD5E1] bg-[#F8FAFC]">
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Chauffeur</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Contact</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Credit TFT</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Statut</th>
                <th className="text-left text-xs font-semibold text-[#64748B] p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((d) => (
                <DriverTableRow key={d.id} d={d} onViewDocs={onViewDocs} onEdit={onEdit} onTft={onTft} onValidate={onValidate} onDelete={onDelete} onImpersonate={onImpersonate} actionLoading={actionLoading} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DriverTableRow({ d, onViewDocs, onEdit, onTft, onValidate, onDelete, onImpersonate, actionLoading }) {
  const badge = getStatusBadge(d.driver_status);
  const tft = d.tft_balance || 0;
  return (
    <tr className="border-b border-[#CBD5E1] last:border-0" data-testid={`all-driver-${d.id}`}>
      <td className="p-3">
        <div className="flex items-center gap-2">
          {d.profile_photo ? (
            <img src={getAuthImgUrl(d.profile_photo)} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#0A2540]/10 flex items-center justify-center"><User className="w-4 h-4 text-[#64748B]" /></div>
          )}
          <span className="text-sm text-[#0A2540] font-medium">{d.name}</span>
        </div>
      </td>
      <td className="p-3">
        <p className="text-xs text-[#64748B]">{d.email}</p>
        <p className="text-xs text-[#64748B] flex items-center gap-1"><Phone className="w-3 h-3" /> {d.phone || "N/A"}</p>
      </td>
      <td className="p-3">
        <span className={`text-sm font-bold ${tft > 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>{tft.toLocaleString()} TFT</span>
      </td>
      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.text}</span></td>
      <td className="p-3">
        <div className="flex gap-1 flex-wrap">
          <Button size="sm" variant="ghost" onClick={() => onViewDocs(d)} className="h-7 px-2 text-xs" data-testid={`view-docs-${d.id}`}><Eye className="w-3 h-3" /></Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit(d)} className="h-7 px-2 text-xs text-[#0A2540]" data-testid={`edit-driver-${d.id}`}><Edit2 className="w-3 h-3" /></Button>
          <Button size="sm" onClick={() => onTft(d)} className="h-7 px-2 text-xs bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308]" data-testid={`recharge-tft-${d.id}`}>
            <Coins className="w-3 h-3 mr-0.5" /> TFT
          </Button>
          {onImpersonate && (
            <Button size="sm" onClick={() => onImpersonate(d)} className="h-7 px-2 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB]" data-testid={`impersonate-driver-${d.id}`} title="Se connecter au compte du chauffeur pour actions correctives">
              <LogIn className="w-3 h-3 mr-0.5" /> Connecter
            </Button>
          )}
          <AdminResetPasswordButton user={{ id: d.id, name: d.name, email: d.email, phone: d.phone, role: "driver" }} variant="icon" testIdPrefix="admin-reset-driver-password" />
          {d.driver_status !== "approved" && (
            <Button size="sm" onClick={() => onValidate(d.id, "approve")} disabled={actionLoading} className="h-7 px-2 text-xs bg-[#10B981] text-white hover:bg-[#059669]">Approuver</Button>
          )}
          {d.driver_status === "approved" && (
            <Button size="sm" variant="outline" onClick={() => onValidate(d.id, "reject")} disabled={actionLoading} className="h-7 px-2 text-xs border-[#EF4444] text-[#EF4444]">Revoquer</Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(d)} className="h-7 px-2 text-xs text-[#EF4444]" data-testid={`delete-driver-${d.id}`}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function DriverAvatar({ driver, badge }) {
  return (
    <div className="flex items-center gap-3">
      {driver.profile_photo ? (
        <img src={getAuthImgUrl(driver.profile_photo)} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#CBD5E1]" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-[#0A2540]/10 flex items-center justify-center"><User className="w-6 h-6 text-[#0A2540]" /></div>
      )}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-base font-semibold text-[#0A2540]">{driver.name}</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.text}</span>
        </div>
        <p className="text-xs text-[#64748B]">{driver.email}</p>
        <p className="text-xs text-[#64748B] flex items-center gap-1"><Phone className="w-3 h-3" /> {driver.phone || "N/A"}</p>
      </div>
    </div>
  );
}
